/**
 * Next.js client-side instrumentation. Runs once at startup before any
 * user code. Wires up Sentry SDK -> GlitchReplay ingest, plus the
 * @glitchreplay/a11y axe-core scanner on dev / staging.
 *
 * Replaces the prior in-house components/GlobalErrorHandler.tsx, which
 * is left in place for the 14-day overlap window per the GlitchReplay
 * migration plan.
 */
import * as Sentry from "@sentry/react";
import { axeIntegration } from "@glitchreplay/a11y";
import { networkProbeIntegration } from "@glitchreplay/network-probe";

const PROJECT_ID = "prj_5d1f18153c42c559335d";
const PUBLIC_KEY = "08d3419f410041c30981edc1d54bd083";
const DSN = `https://${PUBLIC_KEY}@glitchreplay.com/0`;

// Replay privacy/redaction flags. Static defaults match the dashboard's
// "balanced" level (Settings → Replays → Privacy level on glitchreplay.com).
// Subsequent page loads use whatever the dashboard slider was last set to
// — cached from the previous /config fetch below. rrweb masks at snapshot
// time, so a change in the dashboard takes effect at the *next* page load,
// not mid-session.
const PRIVACY_CACHE_KEY = `gr:privacy:${PROJECT_ID}`;
type PrivacyFlags = {
  maskAllText: boolean;
  maskAllInputs: boolean;
  blockAllMedia: boolean;
};
const DEFAULT_PRIVACY: PrivacyFlags = {
  maskAllText: false,
  maskAllInputs: true,
  blockAllMedia: true,
};
function readCachedPrivacy(): PrivacyFlags {
  if (typeof localStorage === "undefined") return DEFAULT_PRIVACY;
  try {
    const raw = localStorage.getItem(PRIVACY_CACHE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<PrivacyFlags>;
      return {
        maskAllText:
          typeof p.maskAllText === "boolean" ? p.maskAllText : DEFAULT_PRIVACY.maskAllText,
        maskAllInputs:
          typeof p.maskAllInputs === "boolean" ? p.maskAllInputs : DEFAULT_PRIVACY.maskAllInputs,
        blockAllMedia:
          typeof p.blockAllMedia === "boolean" ? p.blockAllMedia : DEFAULT_PRIVACY.blockAllMedia,
      };
    }
  } catch {}
  return DEFAULT_PRIVACY;
}
const PRIVACY = readCachedPrivacy();

const NOISE_MESSAGES = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
  // Browser-extension content scripts that double-inject on SPA route changes.
  // We do not declare ScreenshotConfig anywhere in our bundle.
  "Identifier 'ScreenshotConfig' has already been declared",
  // Wallet-extension provider rejections — never reachable from our app.
  "Failed to connect to MetaMask",
  // History API protected by an extension wrapper; we never reassign pushState.
  "Cannot assign to read only property 'pushState' of object",
  // Minified React #310 ("Rendered more hooks than during the previous
  // render") with no in-app stack frames — extension-injected DOM mutating
  // between renders. Filtered when stack-less; real cases will retain a
  // stack and be kept by the existing beforeSend logic.
  "Minified React error #310",
];

// Stack-less or third-party-stack rejection messages thrown from embedded
// widgets / browser quirks rather than our code. Drop when there's no
// usable stack — same logic as the existing "Failed to fetch" filter.
const NOISE_REJECTION_MESSAGES_NO_STACK = [
  "Failed to fetch",
  // Firefox transient network error during cancelled fetch / offline.
  "NetworkError when attempting to fetch resource.",
  // Safari transient network error.
  "Load failed",
  // Apollo / HubSpot / Twilio Flex chat fetch wrappers throw these on
  // upstream blips; without a stack pointing into our code there's
  // nothing to do on our side.
  /^Response not (ok|successful)/i,
  // PromiseRejectionEvent with no reason at all.
  "Unhandled promise rejection",
  // Microsoft Outlook SafeLink / Office link-preview crawler. Its host
  // shell isn't present in a normal browser, so the bridge call to
  // `update` rejects with this fixed string. Always stack-less; the URL
  // is whatever page the email recipient was about to open.
  /^Object Not Found Matching Id:\d+, MethodName:[^,]+, ParamCount:\d+$/,
];

// Third-party tracker / ad / analytics scripts. Errors thrown from frames
// inside these URLs are noise — filter them at the SDK boundary so they
// never hit the wire. Mirrors the legacy GlobalErrorHandler `noisePatterns`
// list, expanded with Zaraz / Cloudflare Insights observed in the wild.
const NOISE_DENY_URLS = [
  /zaraz/i,
  /cloudflareinsights/i,
  /connect\.facebook\.net/i,
  /fbevents/i,
  /googletagmanager/i,
  /googlesyndication/i,
  /fundingchoicesmessages/i,
  /google-analytics/i,
  /clarity\.ms/i,
  /js\.hs-(scripts|banner|analytics|adspixel)/i,
  /js\.hscollectedforms/i,
  /js\.hubspot/i,
  /js\.usemessages/i,
  /analytics\.ahrefs/i,
  /vercel\/insights/i,
  // Embedded Google APIs (gapi loader, Translate widget, gstatic asset host).
  // These don't originate from our pages — usually injected by Translate
  // browser extensions or scraper previews.
  /apis\.google\.com/i,
  /translate\.googleapis\.com/i,
  /gstatic\.com\/.*\/translate/i,
  // Public CDN runtimes pulled in by user-injected snippets / sandboxes
  // (jsdelivr Recharts/jsPDF/prop-types failures appear when an embedder
  // rate-limits or strips the script).
  /cdn\.jsdelivr\.net/i,
  // Browser-extension trackers that piggyback on pages.
  /gtmpx\.com/i,
  /scriptcdn\.net/i,
  /amazingrefresh\.com/i,
  // Third-party icon CDN — replaced with inline SVG / local assets where
  // we used it; remaining refs come from cached pages or scraped previews.
  /img\.icons8\.com/i,
];

Sentry.init({
  dsn: DSN,
  release:
    process.env.NEXT_PUBLIC_RELEASE ||
    process.env.NEXT_PUBLIC_COMMIT_SHA ||
    "dev",
  // Always-on session replay. Buffer mode (`replaysSessionSampleRate: 0`
  // + `replaysOnErrorSampleRate: 1.0`) silently drops first-frame errors:
  // the rrweb full snapshot is queued asynchronously, so when a React
  // hydration error (e.g. #418) fires during the first commit the buffer
  // is empty and the on-error flush sends nothing. Session mode starts
  // the recorder synchronously at init and uploads segments continuously,
  // so even errors thrown before the first snapshot land still produce a
  // viewable replay (post-error frames at minimum). Counter-evidence:
  // gr_xkggqbf, financialtoolset.com, 2026-04-27.
  tracesSampleRate: 0,
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  denyUrls: NOISE_DENY_URLS,
  integrations: [
    Sentry.replayIntegration(PRIVACY),
    axeIntegration({
      runOn: ["development", "staging", "production"],
      // RUM-style sampling: 5% of prod page loads run a single axe scan
      // via requestIdleCallback. Same fingerprint dedup means full rule
      // coverage accumulates across visitors within hours.
      productionSampleRate: 0.05,
      wcagLevel: "AA",
      minImpact: "moderate",
    }),
    // Drops imagedelivery.net "Failed to load <img>" reports caused by
    // ad-blockers / privacy extensions / DNS filters at the network
    // layer. Verifies real failures via per-URL probe so genuine CDN
    // outages still surface (tagged gr.probe=reachable).
    networkProbeIntegration({
      verifyResourceErrors: [/imagedelivery\.net/i],
    }),
    // Captures fetch/XHR 5xx responses. The browser auto-logs failed
    // requests to the console, but `fetch()` doesn't reject on HTTP error
    // status, so no exception or unhandled rejection fires — default
    // integrations miss them. Defaults: status [[500, 599]], targets /.*/.
    Sentry.httpClientIntegration(),
  ],
  beforeSend(event, hint) {
    const message =
      event.message || (hint?.originalException as Error | undefined)?.message || "";
    if (NOISE_MESSAGES.some((n) => message.includes(n))) return null;
    // Stack-less rejections from embedded widgets / browser quirks: drop
    // the message when there's no usable stack so legitimate fetch
    // failures with a real call site still surface.
    const error = hint?.originalException;
    const hasUsableStack = error instanceof Error && !!error.stack;
    if (!hasUsableStack) {
      const matches = NOISE_REJECTION_MESSAGES_NO_STACK.some((p) =>
        typeof p === "string" ? message === p : p.test(message)
      );
      if (matches) return null;
    }
    return event;
  },
});

// Dashboard-controlled session sampling. Static init above starts session
// replay at 1.0 to guarantee first-frame error capture. This side-channel
// lets the dashboard *down-sample* (or stop) the live recorder when a
// project owner wants to dial back replay volume without redeploying — if
// the dashboard returns `enabled: false` or a sub-1.0 sample rate that
// loses the dice roll, we stop the in-progress recording.
type ReplayCfg = {
  replay?: {
    enabled?: boolean;
    sessionSampleRate?: number;
    onErrorSampleRate?: number;
    maskAllText?: boolean;
    maskAllInputs?: boolean;
    blockAllMedia?: boolean;
  };
};

// Session-attempt beacon. Counts every SDK init for the dashboard's
// errors/session denominator — the /config fetch below decides whether
// to *persist* this recording, but every attempt should count. sendBeacon
// survives pagehide so quick-bounce visitors are still tallied, and never
// delays unload.
try {
  navigator.sendBeacon(
    `https://glitchreplay.com/api/${PROJECT_ID}/session-attempt?sentry_key=${PUBLIC_KEY}`,
  );
} catch {}

fetch(
  `https://glitchreplay.com/api/${PROJECT_ID}/config?sentry_key=${PUBLIC_KEY}`,
  { cache: "no-store" },
)
  .then(async (r): Promise<ReplayCfg | null> => (r.ok ? ((await r.json()) as ReplayCfg) : null))
  .then((cfg) => {
    // Cache the dashboard's current privacy flags for next page load.
    if (
      cfg?.replay &&
      typeof cfg.replay.maskAllText === "boolean" &&
      typeof cfg.replay.maskAllInputs === "boolean" &&
      typeof cfg.replay.blockAllMedia === "boolean"
    ) {
      try {
        localStorage.setItem(
          PRIVACY_CACHE_KEY,
          JSON.stringify({
            maskAllText: cfg.replay.maskAllText,
            maskAllInputs: cfg.replay.maskAllInputs,
            blockAllMedia: cfg.replay.blockAllMedia,
          }),
        );
      } catch {}
    }

    const replay = Sentry.getReplay();
    if (!replay) return;
    if (!cfg?.replay?.enabled) {
      replay.stop();
      return;
    }
    const sessionRate = Number(cfg.replay.sessionSampleRate ?? 1);
    if (sessionRate < 1 && Math.random() >= sessionRate) {
      replay.stop();
    }
  })
  .catch(() => {
    // Network/CORS/whatever — leave the static 1.0 session recorder running.
  });

// --- Chunk-load failure auto-reload ---
// Stale tabs that pre-date a deploy reference Next.js chunks whose hashes no
// longer exist. Reloading picks up the current bundle. Anti-loop guard: skip
// if we reloaded within the last 30 s (handles the edge case where the fresh
// bundle also fails, e.g. during a CDN outage or offline).
const CHUNK_RELOAD_GUARD_MS = 30_000;
document.addEventListener(
  "error",
  (event) => {
    const target = event.target as HTMLElement | null;
    if (!target?.tagName) return;
    const tag = target.tagName.toLowerCase();
    if (tag !== "script" && tag !== "link") return;
    const src =
      (target as HTMLScriptElement).src || (target as HTMLLinkElement).href || "";
    if (!src.includes("/_next/static/chunks/") && !src.includes("/_next/static/css/")) return;
    try {
      const last = sessionStorage.getItem("__chunk_reload_attempted");
      if (last && Date.now() - Number(last) < CHUNK_RELOAD_GUARD_MS) return;
      sessionStorage.setItem("__chunk_reload_attempted", String(Date.now()));
    } catch {
      return; // sessionStorage unavailable — skip rather than risk a loop
    }
    window.location.reload();
  },
  true,
);
